/**
 * Single-flight loader for Maps JavaScript API + Places library.
 * Call early (e.g. OrderPage mount) so download runs in parallel with other work.
 */

const GOOGLE_LOAD_TIMEOUT_MS = 12000;
const CALLBACK_NAME = '__sonicGooglePlacesReady';

function apiKey(): string {
  return process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '';
}

let inflight: Promise<boolean> | null = null;

/**
 * Ensures the Places library is loading or loaded. Safe to call from multiple components.
 * Resolves true if Autocomplete can be constructed, false if no key, timeout, or error.
 */
export function ensureGooglePlacesLoaded(): Promise<boolean> {
  const key = apiKey();
  if (!key) return Promise.resolve(false);
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.google?.maps?.places?.Autocomplete) return Promise.resolve(true);
  if (inflight) return inflight;

  inflight = new Promise<boolean>((resolve) => {
    const finish = (ok: boolean) => {
      inflight = null;
      resolve(ok);
    };

    const existing = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      if (window.google?.maps?.places?.Autocomplete) {
        finish(true);
        return;
      }
      const onDone = () => finish(Boolean(window.google?.maps?.places?.Autocomplete));
      existing.addEventListener('load', onDone, { once: true });
      existing.addEventListener('error', () => finish(false), { once: true });
      window.setTimeout(() => finish(Boolean(window.google?.maps?.places?.Autocomplete)), GOOGLE_LOAD_TIMEOUT_MS);
      return;
    }

    const timeoutId = window.setTimeout(() => finish(false), GOOGLE_LOAD_TIMEOUT_MS);

    (window as unknown as Record<string, () => void>)[CALLBACK_NAME] = () => {
      window.clearTimeout(timeoutId);
      finish(Boolean(window.google?.maps?.places?.Autocomplete));
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      key
    )}&libraries=places&loading=async&callback=${CALLBACK_NAME}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      finish(false);
    };
    document.head.appendChild(script);
  });

  return inflight;
}
